import React, { useState, useEffect } from 'react';
import { Sparkles, Send, Copy, Check, CheckCircle2, Loader2, BrainCircuit, ShieldCheck, Zap, TrendingUp, Layers, ArrowRight, HelpCircle, ChevronDown, Info, Globe, Search, KeyRound } from 'lucide-react';
import { executeFlowAction } from '../services/gemini';
import { PageContext } from '../types';
import { auth, db } from '../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, getDocs } from 'firebase/firestore';
import { useToast } from './Toast';

interface LandingPage {
  id: string;
  name: string;
  description?: string;
  content?: {
    headline?: string;
    ctaText?: string;
  };
}

import { markChecklistItemCompleted } from '../src/lib/checklist';

export const SmartCopywriter: React.FC = () => {
  const { showToast } = useToast();
  const [productName, setProductName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [copyResult, setCopyResult] = useState<any>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [userPages, setUserPages] = useState<LandingPage[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<string>('');
  const [isInjecting, setIsInjecting] = useState(false);
  const [injectSuccess, setInjectSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState<'landing' | 'ads'>('landing');
  const [adsResult, setAdsResult] = useState<any>(null);

  useEffect(() => {
    if (!auth.currentUser) return;

    const q = query(
      collection(db, 'landing_pages'),
      where('userId', '==', auth.currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const pages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as LandingPage[];
      setUserPages(pages);
    });

    return () => unsubscribe();
  }, []);

  const handlePageSelect = (pageId: string) => {
    const page = userPages.find(p => p.id === pageId);
    if (page) {
      setProductName(page.name || "");
      setDescription(page.description || "");
      setSelectedPageId(pageId);
      showToast(`Dados da página "${page.name}" carregados!`, 'success');
    }
  };

  const handleGenerate = async () => {
    if (!productName || !description) {
      showToast('Por favor, preencha o nome e a descrição do produto.', 'warning');
      return;
    }

    setLoading(true);
    try {
      const context: PageContext = {
        productName,
        description,
        currentHeadline: '',
        currentCTA: '',
        traffic: 0,
        conversionsA: 0,
        conversionsB: 0,
        ctrA: 0,
        ctrB: 0,
        bounceRate: 0,
        avgTimeOnPage: 0,
        daysActive: 0,
        totalLeads: 0,
        trafficSource: 'Todos',
        deviceType: 'Todos',
        plan: 'pro' // Simulating pro for trial users
      };

      const rawResult = await executeFlowAction(activeTab === 'landing' ? 'gerar_copy_completa' : 'gerar_anuncios_ads', context);
      
      const parsed = JSON.parse(rawResult);

      if (parsed.error) {
        showToast(parsed.error, 'error');
        return;
      }

      if (activeTab === 'landing') {
        setCopyResult(parsed);
        showToast('Copy gerada com sucesso!', 'success');
        markChecklistItemCompleted('copy');
      } else {
        setAdsResult(parsed);
        showToast('Anúncios gerados com sucesso!', 'success');
        markChecklistItemCompleted('ads');
      }
    } catch (error) {
      console.error("AI Generation Error:", error);
      showToast('O motor de IA está temporariamente indisponível. Tente clicar em Gerar novamente.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    if (!text) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.warn('Falha na API Clipboard do Iframe, usando prompt:', err);
      window.prompt('Copie o texto gerado manualmente:', text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const handleInject = async () => {
    if (!selectedPageId || !copyResult) return;

    setIsInjecting(true);
    setInjectSuccess(false);
    try {
      // Find keys case-insensitively or with common variations
      const findVal = (possibleKeys: string[]) => {
        for (const k of possibleKeys) {
          if (copyResult[k]) return copyResult[k];
          // Try lowercase match
          const foundKey = Object.keys(copyResult).find(ck => ck.toLowerCase() === k.toLowerCase());
          if (foundKey) return copyResult[foundKey];
        }
        return '';
      };

      await updateDoc(doc(db, 'landing_pages', selectedPageId), {
        headline: findVal(['Headline', 'Título', 'Titulo', 'headline', 'titulo']),
        subheadline: findVal(['Subheadline', 'Subtítulo', 'Subtitulo', 'subheadline', 'subtitulo']),
        cta: findVal(['CTA', 'Chamada para Ação', 'Botão', 'cta', 'botao']),
        benefits: [
          findVal(['Benefício 1', 'Beneficio 1', 'Benefit 1', 'beneficio1']),
          findVal(['Benefício 2', 'Beneficio 2', 'Benefit 2', 'beneficio2']),
          findVal(['Benefício 3', 'Beneficio 3', 'Benefit 3', 'beneficio3'])
        ],
        problem: findVal(['Problema', 'problema', 'Problem', 'problem']),
        solution: findVal(['Solucao', 'solucao', 'Solution', 'solution', 'Solução']),
        faq: copyResult.FAQ || copyResult.faq || []
      });
      setInjectSuccess(true);
      showToast('Copy injetada na página com sucesso!', 'success');
      setTimeout(() => setInjectSuccess(false), 5000);
    } catch (error) {
      console.error("Error injecting copy:", error);
      showToast('Erro ao injetar copy na página.', 'error');
    } finally {
      setIsInjecting(false);
    }
  };

  return (
    <div className="p-4 lg:p-12 max-w-6xl mx-auto space-y-12 animate-fade-in pb-32">
      <div className="text-center space-y-6">
        <div className="inline-flex items-center gap-3 px-4 py-2 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400 text-[10px] font-bold uppercase tracking-widest mb-4">
          <Sparkles className="w-3 h-3" />
          Powered by Gemini 1.5 Flash
        </div>
        <h1 className="text-4xl lg:text-6xl font-bold text-white tracking-tighter">Redator Publicitário IA</h1>
        <p className="text-xl text-slate-500 max-w-2xl mx-auto font-medium">
          Transforme sua ideia em uma página de vendas de alta conversão em segundos usando a IA Quantum do OptiVelo.
        </p>

        <div className="flex flex-col items-center gap-4 pt-4">
          <div className="flex items-center justify-center gap-4">
            <div className="group relative">
              <button 
                onClick={() => setActiveTab('landing')}
                className={`px-8 py-3 rounded-2xl text-[11px] font-bold uppercase tracking-widest transition-all border ${
                  activeTab === 'landing' 
                    ? 'bg-indigo-500 border-indigo-400 text-white shadow-lg shadow-indigo-500/20' 
                    : 'bg-white/[0.03] border-white/10 text-slate-500 hover:border-white/20'
                }`}
              >
                Landing Page Copy
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[10px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                Gere a estrutura completa de texto para sua página de vendas, focada em persuasão.
              </div>
            </div>
            <div className="group relative">
              <button 
                onClick={() => setActiveTab('ads')}
                className={`px-8 py-3 rounded-2xl text-[11px] font-bold uppercase tracking-widest transition-all border ${
                  activeTab === 'ads' 
                    ? 'bg-indigo-500 border-indigo-400 text-white shadow-lg shadow-indigo-500/20' 
                    : 'bg-white/[0.03] border-white/10 text-slate-500 hover:border-white/20'
                }`}
              >
                Gerador de Anúncios
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[10px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                Crie copies de anúncios para Facebook, Instagram e Google que atraem cliques qualificados.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Central Didática de Copywriting */}
      <div className="premium-card p-6 bg-indigo-500/5 border-indigo-500/10 space-y-4 animate-in fade-in slide-in-from-top-4 duration-500">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/15 flex items-center justify-center text-indigo-400">
            <BrainCircuit className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <h2 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-none">Como Funciona a IA?</h2>
            <h3 className="text-base font-black text-white tracking-tight">O Segredo das Copies que Vendem Milhões no Mercado Brasileiro</h3>
          </div>
        </div>
        <p className="text-xs text-slate-400 leading-relaxed">
          Nossa inteligência não gera meros textos genéricos. Ela aplica o consagrado framework de vendas <strong className="text-indigo-400">AIDA</strong> (Atenção, Interesse, Desejo, Ação) para guiar o olhar de quem entra na página diretamente para o botão de compra. Nossos prompts calibram a IA de forma específica para as dores do mercado brasileiro, criando ofertas honestas, porém irresistíveis, que reduzem a desconfiança do cliente e geram cliques automáticos.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
          <div className="p-3 bg-white/[0.02] border border-white/5 rounded-xl">
            <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest block mb-1">Passo 1: Definir</span>
            <span className="text-xs text-slate-400 leading-relaxed">Insira o nome e a descrição do que está vendendo com o máximo de detalhes possível.</span>
          </div>
          <div className="p-3 bg-white/[0.02] border border-white/5 rounded-xl">
            <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest block mb-1">Passo 2: Gerar</span>
            <span className="text-xs text-slate-400 leading-relaxed">Clique no botão abaixo. A IA criará do zero todos os textos estruturados ou anúncios lucrativos.</span>
          </div>
          <div className="p-3 bg-white/[0.02] border border-white/5 rounded-xl">
            <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest block mb-1">Passo 3: Injetar</span>
            <span className="text-xs text-slate-400 leading-relaxed">Selecione uma de suas páginas existentes e aplique com apenas um clique!</span>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-12 items-start">
        {/* Input Section */}
        <div className="premium-card p-10 space-y-8">
          <div className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <label className="micro-label">Importar de Landing Page</label>
                <div className="group relative">
                  <HelpCircle className="w-3 h-3 text-slate-500 cursor-help hover:text-indigo-400 transition-colors" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[10px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                    Este campo é opcional e serve para atualizar páginas já criadas com a nova copy gerada.
                  </div>
                </div>
              </div>
              <div className="relative">
                <select 
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:border-indigo-500 focus:outline-none appearance-none transition-all"
                  value={selectedPageId}
                  onChange={(e) => handlePageSelect(e.target.value)}
                >
                  <option value="">Selecionar página...</option>
                  {userPages.map(page => (
                    <option key={page.id} value={page.id}>{page.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <label className="micro-label">Nome do Produto/Serviço</label>
                <div className="group relative">
                  <HelpCircle className="w-3 h-3 text-slate-500 cursor-help hover:text-indigo-400 transition-colors" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[10px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                    Insira o nome oficial da sua oferta. Isso ajuda a IA a manter a consistência da marca em toda a copy.
                  </div>
                </div>
              </div>
              <input 
                type="text" 
                placeholder="Ex: OptiVelo"
                className="premium-input"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <label className="micro-label">Descrição Detalhada (O que ele faz e para quem?)</label>
                <div className="group relative">
                  <HelpCircle className="w-3 h-3 text-slate-500 cursor-help hover:text-indigo-400 transition-colors" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 border border-white/10 rounded-lg text-[10px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl">
                    Para melhores resultados, descreva o problema que você resolve, os benefícios únicos e quem é o público-alvo. Detalhes específicos geram copies muito mais persuasivas.
                  </div>
                </div>
              </div>
              <textarea 
                placeholder="Ex: Uma plataforma de IA que otimiza landing pages para infoprodutores e agências..."
                className="premium-input min-h-[120px] max-h-[200px] py-4"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          <div className="sticky bottom-0 pt-4 bg-slate-900/50 backdrop-blur-sm -mx-10 px-10 pb-2 border-t border-white/5 mt-4">
            <button 
              onClick={handleGenerate}
              disabled={loading}
              className="w-full bg-white hover:bg-slate-200 text-black font-bold py-5 rounded-2xl transition-all flex items-center justify-center gap-3 disabled:opacity-50 uppercase tracking-widest text-xs shadow-xl shadow-white/5"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <BrainCircuit className="w-5 h-5" />}
              {loading ? 'Gerando Estratégia...' : activeTab === 'landing' ? 'Gerar Copy de Alta Conversão' : 'Gerar Anúncios de Alta Performance'}
            </button>
          </div>

          <div className="pt-6 border-t border-white/5 grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3 text-slate-500">
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
              <span className="text-[10px] font-bold uppercase tracking-widest">Foco em Conversão</span>
            </div>
            <div className="flex items-center gap-3 text-slate-500">
              <Zap className="w-4 h-4 text-amber-500" />
              <span className="text-[10px] font-bold uppercase tracking-widest">Gatilhos Mentais</span>
            </div>
          </div>
        </div>

        {/* Output Section */}
        <div className="space-y-8">
          {activeTab === 'landing' ? (
            copyResult ? (
              <div className="space-y-6 animate-fade-in">
                <div className="premium-card p-8 space-y-8">
                  <div className="flex justify-between items-center border-b border-white/5 pb-6">
                    <h3 className="text-lg font-bold text-white flex items-center gap-3">
                      <TrendingUp className="w-5 h-5 text-indigo-400" />
                      Estrutura Gerada
                    </h3>
                    <div className="flex items-center gap-2 px-3 py-1 bg-emerald-500/10 rounded-full border border-emerald-500/20">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                      <span className="text-[8px] font-bold text-emerald-500 uppercase tracking-widest">Otimizado</span>
                    </div>
                  </div>

                  <div className="space-y-8">
                    <div className="p-6 bg-white/[0.03] border border-white/10 rounded-2xl space-y-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Layers className="w-4 h-4 text-indigo-400" />
                        <p className="micro-label">Injetar em uma Landing Page</p>
                      </div>
                      <div className="flex gap-3">
                        <select 
                          className="flex-1 bg-black border border-white/10 rounded-xl px-4 py-2 text-xs text-white focus:border-indigo-500 focus:outline-none"
                          value={selectedPageId}
                          onChange={(e) => setSelectedPageId(e.target.value)}
                        >
                          <option value="">Selecionar Página...</option>
                          {userPages.map(page => (
                            <option key={page.id} value={page.id}>{page.name}</option>
                          ))}
                        </select>
                        <button 
                          onClick={handleInject}
                          disabled={!selectedPageId || isInjecting}
                          className={`px-6 py-2 text-white text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all disabled:opacity-50 flex items-center gap-2 ${
                            injectSuccess ? 'bg-emerald-500' : 'bg-indigo-500 hover:bg-indigo-400'
                          }`}
                        >
                          {isInjecting ? <Loader2 className="w-3 h-3 animate-spin" /> : injectSuccess ? <Check className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
                          {injectSuccess ? 'Injetado!' : 'Injetar'}
                        </button>
                      </div>
                      {injectSuccess && (
                        <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center justify-between animate-in fade-in slide-in-from-top-2">
                          <div className="flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                            <p className="text-xs text-emerald-500 font-medium">Copy injetada com sucesso!</p>
                          </div>
                          <a 
                            href={`/p/${selectedPageId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest hover:underline flex items-center gap-1"
                          >
                            Ver Página <ArrowRight className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>

                    <ResultField label="Título" value={copyResult.Headline || copyResult.headline || copyResult.titulo || copyResult.Título} onCopy={() => copyToClipboard(copyResult.Headline || copyResult.headline || copyResult.titulo || copyResult.Título, 'headline')} isCopied={copiedField === 'headline'} />
                    <ResultField label="Subtítulo" value={copyResult.Subheadline || copyResult.subheadline || copyResult.subtitulo || copyResult.Subtítulo} onCopy={() => copyToClipboard(copyResult.Subheadline || copyResult.subheadline || copyResult.subtitulo || copyResult.Subtítulo, 'subheadline')} isCopied={copiedField === 'subheadline'} />
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <ResultField label="O Problema" value={copyResult.Problema || copyResult.problema || ''} onCopy={() => copyToClipboard(copyResult.Problema || copyResult.problema || '', 'problema')} isCopied={copiedField === 'problema'} />
                      <ResultField label="A Solução" value={copyResult.Solucao || copyResult.solucao || ''} onCopy={() => copyToClipboard(copyResult.Solucao || copyResult.solucao || '', 'solucao')} isCopied={copiedField === 'solucao'} />
                    </div>

                    <div className="space-y-4">
                      <p className="micro-label">Benefícios Principais</p>
                      <div className="grid gap-3">
                        <BenefitItem text={copyResult['Benefício 1'] || copyResult['beneficio1'] || copyResult['Benefit 1'] || copyResult['Beneficio 1']} />
                        <BenefitItem text={copyResult['Benefício 2'] || copyResult['beneficio2'] || copyResult['Benefit 2'] || copyResult['Beneficio 2']} />
                        <BenefitItem text={copyResult['Benefício 3'] || copyResult['beneficio3'] || copyResult['Benefit 3'] || copyResult['Beneficio 3']} />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <ResultField label="Chamada para Ação (CTA)" value={copyResult.ctaText || copyResult.CTA || copyResult.cta || copyResult.botao || copyResult.Botão || ''} onCopy={() => copyToClipboard(copyResult.ctaText || copyResult.CTA || copyResult.cta || copyResult.botao || copyResult.Botão || '', 'cta')} isCopied={copiedField === 'cta'} />
                      <ResultField label="Garantia" value={copyResult.garantia || copyResult.Garantia || ''} onCopy={() => copyToClipboard(copyResult.garantia || copyResult.Garantia || '', 'garantia')} isCopied={copiedField === 'garantia'} />
                    </div>

                    {(copyResult.FAQ || copyResult.faq) && Array.isArray(copyResult.FAQ || copyResult.faq) && (
                      <div className="space-y-4">
                        <p className="micro-label">Sugestão de FAQ</p>
                        <div className="grid gap-4">
                          {(copyResult.FAQ || copyResult.faq).map((item: any, i: number) => (
                            <div key={i} className="p-4 bg-white/[0.02] border border-white/5 rounded-xl space-y-2">
                              <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">{item.pergunta}</p>
                              <p className="text-xs text-slate-400 leading-relaxed">{item.resposta}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="p-6 bg-indigo-500/5 border border-indigo-500/10 rounded-2xl space-y-3">
                      <p className="micro-label text-indigo-400">Raciocínio da IA</p>
                      <p className="text-xs text-slate-400 leading-relaxed italic">"{copyResult.Motivo || copyResult.motivo || copyResult.reason}"</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full min-h-[400px] border-2 border-dashed border-white/5 rounded-[40px] flex flex-col items-center justify-center text-center p-12 space-y-6">
                <div className="w-20 h-20 bg-white/5 rounded-3xl flex items-center justify-center text-slate-700">
                  <Sparkles className="w-10 h-10" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-slate-500">Aguardando seu Comando</h3>
                  <p className="text-sm text-slate-600 max-w-xs mx-auto">Preencha os dados ao lado para que a IA possa criar sua estratégia de vendas.</p>
                </div>
              </div>
            )
          ) : (
            adsResult ? (
              <div className="space-y-6 animate-fade-in">
                <div className="premium-card p-8 space-y-8">
                  <div className="flex justify-between items-center border-b border-white/5 pb-6">
                    <h3 className="text-lg font-bold text-white flex items-center gap-3">
                      <TrendingUp className="w-5 h-5 text-indigo-400" />
                      Anúncios Gerados
                    </h3>
                  </div>

                  <div className="space-y-12">
                    {/* Facebook/Instagram */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <Globe className="w-4 h-4 text-blue-400" />
                        </div>
                        <h4 className="text-sm font-bold text-white uppercase tracking-widest">Facebook & Instagram</h4>
                      </div>
                      <div className="grid gap-8">
                        {Array.isArray(adsResult.facebook) ? (
                          adsResult.facebook.map((ad: any, idx: number) => (
                            <div key={idx} className="space-y-4 p-6 bg-white/[0.02] border border-white/5 rounded-2xl relative group/ad">
                              <div className="absolute -top-3 left-6 px-3 py-1 bg-indigo-500 text-white text-[8px] font-bold uppercase tracking-widest rounded-full shadow-lg">
                                Variação {idx + 1}
                              </div>
                              <ResultField 
                                label="Texto Principal (Copy)" 
                                value={ad.primaryText || ad.text || ''} 
                                onCopy={() => copyToClipboard(ad.primaryText || ad.text || '', `fb_text_${idx}`)} 
                                isCopied={copiedField === `fb_text_${idx}`} 
                              />
                              <ResultField 
                                label="Título do Anúncio" 
                                value={ad.headline || ''} 
                                onCopy={() => copyToClipboard(ad.headline || '', `fb_headline_${idx}`)} 
                                isCopied={copiedField === `fb_headline_${idx}`} 
                              />
                            </div>
                          ))
                        ) : (
                          <div className="space-y-4 p-6 bg-white/[0.02] border border-white/5 rounded-2xl">
                            <ResultField 
                              label="Texto Principal (Copy)" 
                              value={adsResult.facebook?.primaryText || adsResult.facebook?.text || ''} 
                              onCopy={() => copyToClipboard(adsResult.facebook?.primaryText || adsResult.facebook?.text || '', 'fb_text')} 
                              isCopied={copiedField === 'fb_text'} 
                            />
                            <ResultField 
                              label="Título do Anúncio" 
                              value={adsResult.facebook?.headline || ''} 
                              onCopy={() => copyToClipboard(adsResult.facebook?.headline || '', 'fb_headline')} 
                              isCopied={copiedField === 'fb_headline'} 
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Google Ads */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                          <Search className="w-4 h-4 text-amber-400" />
                        </div>
                        <h4 className="text-sm font-bold text-white uppercase tracking-widest">Google Ads (Pesquisa)</h4>
                      </div>
                      <div className="grid gap-6">
                        <div className="space-y-4">
                          <p className="micro-label">Títulos (Headlines)</p>
                          <div className="grid gap-3">
                            {(adsResult.google?.headlines || []).map((h: string, i: number) => (
                              <div key={i} className="flex items-center justify-between p-3 bg-white/[0.02] border border-white/5 rounded-xl">
                                <span className="text-xs text-slate-300">{h}</span>
                                <button onClick={() => copyToClipboard(h, `g_h_${i}`)} className="text-slate-500 hover:text-white transition-colors">
                                  {copiedField === `g_h_${i}` ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                        <ResultField 
                          label="Descrição" 
                          value={adsResult.google?.description || ''} 
                          onCopy={() => copyToClipboard(adsResult.google?.description || '', 'g_desc')} 
                          isCopied={copiedField === 'g_desc'} 
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full min-h-[400px] border-2 border-dashed border-white/5 rounded-[40px] flex flex-col items-center justify-center text-center p-12 space-y-6">
                <div className="w-20 h-20 bg-white/5 rounded-3xl flex items-center justify-center text-slate-700">
                  <Zap className="w-10 h-10" />
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <h3 className="text-xl font-bold text-slate-500">Gerador de Anúncios</h3>
                    <p className="text-sm text-slate-600 max-w-xs mx-auto">Crie anúncios persuasivos para Facebook, Instagram e Google em segundos.</p>
                  </div>
                  
                  {productName && description && (
                    <button 
                      onClick={handleGenerate}
                      disabled={loading}
                      className="px-6 py-3 bg-indigo-500 text-white rounded-xl font-bold text-[10px] uppercase tracking-widest hover:bg-indigo-400 transition-all flex items-center gap-2 mx-auto"
                    >
                      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                      Gerar Anúncios Agora
                    </button>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

const ResultField = ({ label, value, onCopy, isCopied }: { label: string, value: string, onCopy: () => void, isCopied: boolean }) => (
  <div className="space-y-2 group">
    <div className="flex justify-between items-center">
      <label className="micro-label">{label}</label>
      <button 
        onClick={onCopy}
        className="p-2 text-slate-500 hover:text-white transition-colors flex items-center gap-2"
      >
        {isCopied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
        <span className="text-[8px] font-bold uppercase tracking-widest">{isCopied ? 'Copiado' : 'Copiar'}</span>
      </button>
    </div>
    <div className="p-4 bg-white/[0.03] border border-white/5 rounded-xl text-sm text-white font-medium leading-relaxed">
      {value}
    </div>
  </div>
);

const BenefitItem = ({ text }: { text: string }) => (
  <div className="flex items-center gap-3 p-3 bg-white/[0.02] border border-white/5 rounded-xl">
    <div className="w-6 h-6 bg-indigo-500/10 rounded-lg flex items-center justify-center text-indigo-400">
      <Check className="w-3 h-3" />
    </div>
    <span className="text-xs text-slate-300 font-medium">{text}</span>
  </div>
);
